import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Button,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Stack,
  CircularProgress,
  Autocomplete,
  Tooltip,
  IconButton,
  LinearProgress
} from "@mui/material";
import {
  CheckCircleOutline,
  RestartAlt as ResetIcon,
  DescriptionOutlined as DocIcon,
  Refresh as RefreshIcon,
  SchoolOutlined as DomainIcon,
  EditOutlined as EditIcon,
  Close as CancelIcon,
  AttachFile as AttachIcon,
  CloudUpload as UploadIcon,
  Visibility as PreviewIcon,
  DeleteOutline as DeleteIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  FileDownload as DownloadIcon
} from "@mui/icons-material";

// BlockNote Imports
import "@blocknote/core/fonts/inter.css";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import "@blocknote/mantine/style.css";
import { useTrainerDomains } from "../../hooks/useTrainers";
import {
  useEventPrerequisites,
  useCreateEventPrerequisite,
  useUpdateEventPrerequisite,
  useDeleteEventPrerequisiteUpload
} from "../../hooks/useEventPrerequisites";
import {
  getPrerequisitePresignedUploadUrl,
  uploadFileToS3,
  getFilePresignedViewUrl,
  deleteS3Object
} from "../../services/eventUploadsService";
import downloadFileFromUrl from "../../utils/fileDownloader";
import FilePreviewLightbox from "../Desiner/UpcomingEvents/components/FilePreviewLightbox";
import DeleteAttachmentConfirmDialog from "./components/DeleteAttachmentConfirmDialog";

// Helper to render inline text styles (bold, italic, code, link, etc.)
const renderInlineContent = (contentArray) => {
  if (!Array.isArray(contentArray) || contentArray.length === 0) return "";
  return contentArray
    .map((item) => {
      if (!item) return "";
      let text = item.text || "";
      if (item.type === "link") {
        const linkText = renderInlineContent(item.content);
        return `<a href="${item.href || "#"}" target="_blank" style="color:#1877f2; text-decoration:underline; font-weight:600;">${linkText || item.href}</a>`;
      }
      // Escape HTML entities
      text = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

      const styles = item.styles || {};
      if (styles.bold) text = `<strong style="font-weight:700; color:#0f172a;">${text}</strong>`;
      if (styles.italic) text = `<em style="font-style:italic;">${text}</em>`;
      if (styles.underline) text = `<u style="text-decoration:underline;">${text}</u>`;
      if (styles.strike) text = `<s style="text-decoration:line-through;">${text}</s>`;
      if (styles.code) {
        text = `<code style="background-color:#f1f5f9; color:#0f172a; padding:2px 6px; border-radius:4px; font-family:monospace; font-size:13px; border:1px solid #e2e8f0;">${text}</code>`;
      }
      if (styles.textColor) {
        text = `<span style="color:${styles.textColor};">${text}</span>`;
      }
      return text;
    })
    .join("");
};

// Helper to extract key_name from an S3 URL or from the url-to-key cache
export const extractKeyNameFromUrl = (url, urlToKeyMap) => {
  if (!url || typeof url !== "string") return url;

  // 1. Check if we have an explicit mapping
  if (urlToKeyMap && urlToKeyMap.has(url)) {
    return urlToKeyMap.get(url);
  }

  // Check without query string
  const urlWithoutQuery = url.split("?")[0];
  if (urlToKeyMap && urlToKeyMap.has(urlWithoutQuery)) {
    return urlToKeyMap.get(urlWithoutQuery);
  }

  // 2. If it is already a key_name (doesn't start with http:// or https:// or data: or blob:)
  if (!/^https?:\/\//i.test(url) && !/^data:/i.test(url) && !/^blob:/i.test(url)) {
    return url;
  }

  // 3. Fallback: Parse URL pathname for S3 URLs
  try {
    const parsed = new URL(url);
    const pathname = decodeURIComponent(parsed.pathname.replace(/^\/+/, ""));
    if (pathname.includes("prerequisites/")) {
      const idx = pathname.indexOf("prerequisites/");
      return pathname.substring(idx);
    }
    if (pathname.includes("event_uploads/")) {
      const idx = pathname.indexOf("event_uploads/");
      return pathname.substring(idx);
    }
    return pathname;
  } catch {
    return url;
  }
};

// Recursively convert block image/file URLs from presigned URLs to S3 key_names for saving
export const convertBlocksUrlsToKeys = (blocks = [], urlToKeyMap) => {
  if (!Array.isArray(blocks)) return blocks;
  return blocks.map((block) => {
    const newBlock = { ...block };
    if (newBlock.props) {
      newBlock.props = { ...newBlock.props };
      if ((newBlock.type === "image" || newBlock.type === "file") && newBlock.props.url) {
        newBlock.props.url = extractKeyNameFromUrl(newBlock.props.url, urlToKeyMap);
      }
    }
    if (Array.isArray(newBlock.children) && newBlock.children.length > 0) {
      newBlock.children = convertBlocksUrlsToKeys(newBlock.children, urlToKeyMap);
    }
    return newBlock;
  });
};

// Recursively resolve block image/file key_names to fresh presigned view URLs for rendering
export const resolveBlocksKeysToUrls = async (blocks = [], urlToKeyMap, keyToUrlMap) => {
  if (!Array.isArray(blocks)) return blocks;

  const resolved = await Promise.all(
    blocks.map(async (block) => {
      const newBlock = { ...block };
      if (newBlock.props) {
        newBlock.props = { ...newBlock.props };
        if ((newBlock.type === "image" || newBlock.type === "file") && newBlock.props.url) {
          const rawUrlOrKey = newBlock.props.url;
          const keyName = extractKeyNameFromUrl(rawUrlOrKey, urlToKeyMap);

          let viewUrl = keyToUrlMap ? keyToUrlMap.get(keyName) : null;
          if (!viewUrl) {
            try {
              viewUrl = await getFilePresignedViewUrl(keyName, false);
            } catch (err) {
              console.warn(`Could not resolve presigned URL for key: ${keyName}`, err);
              viewUrl = rawUrlOrKey;
            }
            if (keyToUrlMap) {
              keyToUrlMap.set(keyName, viewUrl || rawUrlOrKey);
            }
          }

          if (viewUrl) {
            newBlock.props.url = viewUrl;
            if (urlToKeyMap) {
              urlToKeyMap.set(viewUrl, keyName);
              urlToKeyMap.set(viewUrl.split("?")[0], keyName);
            }
          }
        }
      }

      if (Array.isArray(newBlock.children) && newBlock.children.length > 0) {
        newBlock.children = await resolveBlocksKeysToUrls(
          newBlock.children,
          urlToKeyMap,
          keyToUrlMap
        );
      }

      return newBlock;
    })
  );

  return resolved;
};

// Convert BlockNote blocks directly to styled HTML for preview and mail dispatch
export const convertBlocksToStyledHtml = (blocks = []) => {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return "";
  }

  const htmlParts = [];
  let inBulletList = false;
  let inNumberedList = false;

  const closeOpenLists = () => {
    if (inBulletList) {
      htmlParts.push("</ul>");
      inBulletList = false;
    }
    if (inNumberedList) {
      htmlParts.push("</ol>");
      inNumberedList = false;
    }
  };

  blocks.forEach((block) => {
    const type = block.type;
    const content = renderInlineContent(block.content);

    if (type === "bulletListItem") {
      if (inNumberedList) closeOpenLists();
      if (!inBulletList) {
        htmlParts.push(
          '<ul style="list-style-type:disc !important; padding-left:24px !important; margin:10px 0 !important; color:#334155;">'
        );
        inBulletList = true;
      }
      htmlParts.push(
        `  <li style="margin-bottom:6px; font-size:14px; line-height:1.6; color:#334155;">${content || "&nbsp;"}</li>`
      );
      return;
    }

    if (type === "numberedListItem") {
      if (inBulletList) closeOpenLists();
      if (!inNumberedList) {
        htmlParts.push(
          '<ol style="list-style-type:decimal !important; padding-left:24px !important; margin:10px 0 !important; color:#334155;">'
        );
        inNumberedList = true;
      }
      htmlParts.push(
        `  <li style="margin-bottom:6px; font-size:14px; line-height:1.6; color:#334155;">${content || "&nbsp;"}</li>`
      );
      return;
    }

    // Any non-list block closes active lists
    closeOpenLists();

    if (type === "heading") {
      const level = block.props?.level || 1;
      if (level === 1) {
        htmlParts.push(
          `<h1 style="font-size:22px; font-weight:700; color:#0f172a; margin-top:24px; margin-bottom:12px; line-height:1.3; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; border-bottom:2px solid #1877f2; padding-bottom:8px;">${content}</h1>`
        );
      } else if (level === 2) {
        htmlParts.push(
          `<h2 style="font-size:17px; font-weight:700; color:#1e293b; margin-top:20px; margin-bottom:8px; line-height:1.35; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">${content}</h2>`
        );
      } else {
        htmlParts.push(
          `<h3 style="font-size:15px; font-weight:600; color:#334155; margin-top:16px; margin-bottom:6px; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">${content}</h3>`
        );
      }
    } else if (type === "paragraph") {
      if (!content.trim()) {
        if (htmlParts.length > 0) {
          htmlParts.push('<div style="height:10px;"></div>');
        }
      } else {
        htmlParts.push(
          `<p style="font-size:14px; line-height:1.65; color:#334155; margin-top:6px; margin-bottom:10px; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">${content}</p>`
        );
      }
    } else if (type === "checkListItem") {
      const isChecked = block.props?.checked;
      htmlParts.push(
        `<div style="display:flex; align-items:flex-start; gap:8px; margin:6px 0; font-size:14px; color:#334155;">
          <span style="display:inline-block; width:16px; height:16px; border:1.5px solid ${isChecked ? "#16a34a" : "#cbd5e1"}; background-color:${isChecked ? "#16a34a" : "#ffffff"}; border-radius:4px; color:#ffffff; text-align:center; line-height:14px; font-size:11px; font-weight:bold; margin-top:3px;">${isChecked ? "✓" : ""}</span>
          <span style="${isChecked ? "text-decoration:line-through; color:#94a3b8;" : ""}">${content}</span>
        </div>`
      );
    } else if (type === "image") {
      const url = block.props?.url;
      const caption = block.props?.caption;
      if (url) {
        htmlParts.push(
          `<div style="margin:16px 0; text-align:center;">
            <img src="${url}" alt="${caption || "Image"}" style="max-width:100%; height:auto; border-radius:8px; border:1px solid #e2e8f0; box-shadow:0 1px 4px rgba(0,0,0,0.05);" />
            ${caption ? `<div style="font-size:12px; color:#64748b; margin-top:4px;">${caption}</div>` : ""}
          </div>`
        );
      }
    } else if (type === "file") {
      const url = block.props?.url;
      const name = block.props?.name || "Attached File";
      if (url) {
        htmlParts.push(
          `<div style="margin:12px 0; display:inline-flex; align-items:center; gap:8px; padding:8px 14px; background-color:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; font-size:13px; font-weight:600;">
            <span>📎</span>
            <a href="${url}" target="_blank" rel="noopener noreferrer" style="color:#1877f2; text-decoration:none;">${name}</a>
          </div>`
        );
      }
    } else {
      if (content) {
        htmlParts.push(
          `<p style="font-size:14px; line-height:1.6; color:#334155; margin:6px 0;">${content}</p>`
        );
      }
    }
  });

  closeOpenLists();
  const rawHtml = htmlParts.join("\n");
  if (!hasMeaningfulEditorContent(rawHtml)) {
    return "";
  }
  return rawHtml;
};

// Check if HTML content contains meaningful text, headings, or media
export const hasMeaningfulEditorContent = (html) => {
  if (!html || typeof html !== "string") return false;
  if (html.includes("No prerequisites specified.")) return false;
  // Strip out empty height spacer divs like <div style="height:10px;"></div>
  const withoutSpacers = html.replace(/<div\s+style="height:\s*\d+px;?"\s*>\s*<\/div>/gi, "").trim();
  if (!withoutSpacers) return false;
  if (/<(img|iframe|video|audio|table|svg)\b/i.test(withoutSpacers)) {
    return true;
  }
  const text = withoutSpacers
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&#160;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > 0;
};

const TrainerDocumentEditor = ({
  initialContent = null,
  onSaveContent,
  title = "Domain Prerequisites Editor",
  readOnly = false,
  role = "trainer",
  domainId: propDomainId = null,
  domainName: propDomainName = null
}) => {
  // Fetch assigned domains for the logged-in trainer via React Query (disabled if explicit domainId is passed)
  const { data: domainsList = [], isLoading: isLoadingDomains } = useTrainerDomains({
    enabled: !propDomainId
  });
  const [selectedDomain, setSelectedDomain] = useState(() =>
    propDomainId
      ? { domain_id: propDomainId, domain_name: propDomainName || "Domain" }
      : null
  );

  // Sync selectedDomain when propDomainId changes or first domain loads
  useEffect(() => {
    if (propDomainId) {
      setSelectedDomain((prev) => {
        if (
          prev &&
          String(prev.domain_id) === String(propDomainId) &&
          prev.domain_name === (propDomainName || "Domain")
        ) {
          return prev;
        }
        return {
          domain_id: propDomainId,
          domain_name: propDomainName || "Domain"
        };
      });
    } else if (domainsList.length > 0) {
      setSelectedDomain((prev) => prev || domainsList[0]);
    }
  }, [propDomainId, propDomainName, domainsList]);

  const selectedDomainId = selectedDomain?.domain_id || selectedDomain?.id;

  // Fetch Event Prerequisites for the currently selected domain from Backend API
  const {
    data: domainPrereqsList = [],
    isLoading: isLoadingPrereq,
    refetch: refetchDomainPrereq
  } = useEventPrerequisites(selectedDomainId, {
    enabled: Boolean(selectedDomainId)
  });

  // Mutation for creating/updating prerequisites
  const { mutateAsync: savePrerequisiteMutation, isLoading: isSavingPrereq } =
    useCreateEventPrerequisite();
  const { mutateAsync: updatePrerequisiteMutation, isLoading: isUpdatingPrereq } =
    useUpdateEventPrerequisite();
  const { mutateAsync: deleteUploadMutation } =
    useDeleteEventPrerequisiteUpload();

  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const lastLoadedKeyRef = useRef(null);

  // Editor and View/Edit Mode states (Default is View mode)
  const [isEditing, setIsEditing] = useState(false);
  const [documentTitle, setDocumentTitle] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasExistingPrereq, setHasExistingPrereq] = useState(false);

  // S3 File Upload States
  const [stagedUploads, setStagedUploads] = useState([]); // [ { file_name, key_name, file_size } ]
  const [existingUploads, setExistingUploads] = useState([]); // Uploads loaded from backend
  const [uploadingProgress, setUploadingProgress] = useState({}); // { [fileName]: progressPercent }

  const isSpoc = Boolean(role === "spoc" || readOnly);
  const hasHtmlContent = hasMeaningfulEditorContent(previewHtml);
  const hasFiles = existingUploads.length > 0 || stagedUploads.length > 0;

  // Delete Confirmation Dialog State
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    file: null,
    isDeleting: false
  });

  // In-Editor Image URL <-> S3 key_name caches
  const urlToKeyMapRef = React.useRef(new Map());
  const keyToUrlMapRef = React.useRef(new Map());

  // In-App File Preview Lightbox State
  const [lightboxFile, setLightboxFile] = useState(null);

  // Snackbar Notification
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  // Create BlockNote editor instance with S3 Presigned URL Upload handler
  const editor = useCreateBlockNote({
    initialContent: initialContent || undefined,
    uploadFile: async (file) => {
      try {
        // Step 1: Call GET /event_uploads/prerequisites/presigned-upload-url
        const { presigned_url, key_name } = await getPrerequisitePresignedUploadUrl(file.name);
        if (!presigned_url || !key_name) {
          throw new Error("Failed to receive presigned upload URL from backend.");
        }

        // Step 2: PUT file binary directly to AWS S3
        await uploadFileToS3(presigned_url, file);

        // Step 3: Fetch presigned view URL for rendering inside the editor canvas
        const viewUrl = await getFilePresignedViewUrl(key_name, false);
        const finalUrl = viewUrl || presigned_url;

        // Cache the mapping between live presigned URL and persistent S3 key_name
        urlToKeyMapRef.current.set(finalUrl, key_name);
        urlToKeyMapRef.current.set(finalUrl.split("?")[0], key_name);
        keyToUrlMapRef.current.set(key_name, finalUrl);

        return finalUrl;
      } catch (err) {
        console.error("Error in BlockNote editor S3 upload:", err);
        setSnackbar({
          open: true,
          message: err?.message || "Failed to upload image to S3.",
          severity: "error"
        });
        throw err;
      }
    }
  });

  // Handler for uploading files / documents from the Attachments section
  const handleAttachmentUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      try {
        setUploadingProgress((prev) => ({ ...prev, [file.name]: 5 }));

        // Step 1: Get presigned upload URL from backend
        const { presigned_url, key_name } = await getPrerequisitePresignedUploadUrl(file.name);
        if (!presigned_url || !key_name) {
          throw new Error(`Failed to get presigned URL for ${file.name}`);
        }

        // Step 2: PUT binary directly to S3 with live upload progress
        await uploadFileToS3(presigned_url, file, (progress) => {
          setUploadingProgress((prev) => ({ ...prev, [file.name]: progress }));
        });

        // Step 3: Add to staged uploads for the separate attached files section
        setStagedUploads((prev) => [
          ...prev,
          {
            file_name: file.name,
            key_name,
            file_size: file.size,
            uploaded_at: new Date().toISOString()
          }
        ]);

        setSnackbar({
          open: true,
          message: `Uploaded "${file.name}" to S3 successfully.`,
          severity: "success"
        });
      } catch (err) {
        console.error(`Error uploading ${file.name} to S3:`, err);
        setSnackbar({
          open: true,
          message: err?.message || `Failed to upload "${file.name}" to S3.`,
          severity: "error"
        });
      } finally {
        setUploadingProgress((prev) => {
          const updated = { ...prev };
          delete updated[file.name];
          return updated;
        });
      }
    }
    event.target.value = "";
  };

  // Open confirmation modal for deleting an existing uploaded file or a newly staged upload
  const handleDeleteUpload = (file, isStaged = false) => {
    setDeleteConfirmDialog({
      open: true,
      file: { ...file, isStaged },
      isDeleting: false
    });
  };

  // Confirm delete of existing DB file via DELETE /event_uploads/event-prerequisite-upload-delete/{upload_id}
  // OR delete staged file from AWS S3 via DELETE /event_uploads/s3/delete-object?key={key}
  const handleConfirmDeleteUpload = async () => {
    const file = deleteConfirmDialog.file;
    if (!file) return;

    try {
      setDeleteConfirmDialog((prev) => ({ ...prev, isDeleting: true }));

      if (file.id) {
        // 1. Existing DB upload: delete row and its S3 object
        await deleteUploadMutation(file.id);
        setExistingUploads((prev) =>
          prev.filter((u) => u.id !== file.id && u.key_name !== file.key_name)
        );
      } else if (file.key_name) {
        // 2. Newly staged upload (uploaded to S3, but not yet saved in DB): delete directly from S3
        await deleteS3Object(file.key_name);
        setStagedUploads((prev) =>
          prev.filter((u) => u.key_name !== file.key_name)
        );
      }

      setDeleteConfirmDialog({ open: false, file: null, isDeleting: false });

      setSnackbar({
        open: true,
        message: `Attachment "${file.file_name}" deleted from S3 successfully.`,
        severity: "success"
      });
    } catch (err) {
      console.error("Error deleting upload attachment:", err);
      setDeleteConfirmDialog((prev) => ({ ...prev, isDeleting: false }));
      setSnackbar({
        open: true,
        message: err?.message || "Failed to delete attachment.",
        severity: "error"
      });
    }
  };

  // Preview attached file using S3 Presigned View URL in FilePreviewLightbox (supports images, pdf, docx, etc.)
  const handlePreviewFile = async (file) => {
    if (!file?.key_name) return;
    try {
      const url = await getFilePresignedViewUrl(file.key_name, false);
      if (!url) {
        throw new Error("Could not retrieve presigned preview URL");
      }
      setLightboxFile({
        id: file.id || file.key_name,
        name: file.file_name,
        url: url,
        size: file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : "Uploaded File",
        uploadedAt: file.uploaded_at || "Recently",
        key_name: file.key_name
      });
    } catch (err) {
      console.error("Error fetching presigned preview URL:", err);
      setSnackbar({
        open: true,
        message: "Failed to preview file from S3.",
        severity: "error"
      });
    }
  };

  // Download attached file using S3 Presigned View URL with download=true
  const handleDownloadFile = async (file) => {
    if (!file?.key_name) return;
    try {
      const url = await getFilePresignedViewUrl(file.key_name, true, file.file_name);
      if (!url) {
        throw new Error("Could not retrieve presigned download URL");
      }
      await downloadFileFromUrl(url, file.file_name || "document");
    } catch (err) {
      console.error("Error downloading file from S3:", err);
      setSnackbar({
        open: true,
        message: "Failed to download file.",
        severity: "error"
      });
    }
  };

  // Load Content when domainPrereqsList from React Query updates or domain changes
  useEffect(() => {
    if (!editor || !selectedDomainId) return;

    const existingPrereq = domainPrereqsList?.[0];
    const meta = existingPrereq?.meta_data;
    const currentKey = `${selectedDomainId}_${existingPrereq?.id || "none"}_${meta?.updated_at || ""}_${(meta?.blocks_json || []).length}`;

    if (lastLoadedKeyRef.current === currentKey) {
      return;
    }
    lastLoadedKeyRef.current = currentKey;

    let isMounted = true;
    setIsLoadingContent(true);

    const loadDomainContent = async () => {
      try {
        const uploads = existingPrereq?.uploads || existingPrereq?.files || [];

        if (!isMounted) return;
        setExistingUploads(uploads);
        setStagedUploads([]);

        if (meta && (meta.blocks_json || meta.html_content)) {
          setDocumentTitle(meta.title || "");
          setHasExistingPrereq(true);

          if (Array.isArray(meta.blocks_json) && meta.blocks_json.length > 0) {
            // Resolve any key_names stored in blocks to fresh presigned view URLs for live display
            const resolvedBlocks = await resolveBlocksKeysToUrls(
              meta.blocks_json,
              urlToKeyMapRef.current,
              keyToUrlMapRef.current
            );
            if (!isMounted) return;

            editor.replaceBlocks(editor.document, resolvedBlocks);
            const html = convertBlocksToStyledHtml(resolvedBlocks);
            setPreviewHtml(html);
          } else if (meta.html_content) {
            const blocks = await editor.tryParseHTMLToBlocks(meta.html_content);
            const resolvedBlocks = await resolveBlocksKeysToUrls(
              blocks,
              urlToKeyMapRef.current,
              keyToUrlMapRef.current
            );
            if (!isMounted) return;

            editor.replaceBlocks(editor.document, resolvedBlocks);
            const html = convertBlocksToStyledHtml(resolvedBlocks);
            setPreviewHtml(html);
          }
          setIsEditing(false); // Default to View mode
        } else {
          // No prerequisites in database: start completely blank/empty
          setDocumentTitle("");
          setHasExistingPrereq(false);
          setPreviewHtml("");
          editor.replaceBlocks(editor.document, [
            {
              type: "paragraph",
              content: []
            }
          ]);
          setIsEditing(false);
        }
      } catch (err) {
        console.error("Error setting editor content from prerequisites data:", err);
      } finally {
        if (isMounted) {
          setIsLoadingContent(false);
        }
      }
    };

    loadDomainContent();

    return () => {
      isMounted = false;
    };
  }, [domainPrereqsList, selectedDomainId, editor, selectedDomain?.domain_name]);

  // Clear Content in Edit Mode
  const handleClear = () => {
    if (editor) {
      editor.replaceBlocks(editor.document, [
        {
          type: "paragraph",
          content: []
        }
      ]);
      setDocumentTitle("");
    }
    setStagedUploads([]);
    setSnackbar({
      open: true,
      message: "Editor canvas cleared.",
      severity: "info"
    });
  };

  // Save / Update Prerequisites via POST or PATCH /event_uploads/event-prerequisites API
  const handleSavePrerequisites = async () => {
    if (!editor || !selectedDomain) {
      setSnackbar({
        open: true,
        message: "Please select a domain before saving prerequisites.",
        severity: "warning"
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const liveBlocks = editor.document;
      const domainId = Number(selectedDomain.domain_id || selectedDomain.id);
      const titleToSave = documentTitle.trim();
      
      // Convert block image/file URLs from temporary presigned URLs to persistent S3 key_name
      const blocksWithKeynames = convertBlocksUrlsToKeys(liveBlocks, urlToKeyMapRef.current);
      
      const liveStyledHtml = convertBlocksToStyledHtml(liveBlocks);
      const styledHtmlWithKeys = convertBlocksToStyledHtml(blocksWithKeynames);
      
      const hasActualContent = hasMeaningfulEditorContent(liveStyledHtml);
      const finalHtml = hasActualContent ? styledHtmlWithKeys : "";
      const finalBlocks = hasActualContent ? blocksWithKeynames : [];
      const now = new Date().toISOString();

      const metaData = {
        title: titleToSave,
        html_content: finalHtml,
        styled_html: finalHtml,
        blocks_json: finalBlocks,
        updated_at: now
      };

      const newUploadsPayload = stagedUploads.map((u) => ({
        file_name: u.file_name,
        key_name: u.key_name
      }));

      const existingPrereq = domainPrereqsList?.[0];
      const existingPrereqId = existingPrereq?.id;

      if (existingPrereqId) {
        // Call PATCH /event_uploads/event-prerequisites/{prerequisite_id}
        await updatePrerequisiteMutation({
          prerequisite_id: existingPrereqId,
          domain_id: domainId,
          meta_data: metaData,
          uploads: newUploadsPayload
        });
      } else {
        // Call POST /event_uploads/event-prerequisites
        await savePrerequisiteMutation({
          domain_id: domainId,
          meta_data: metaData,
          uploads: newUploadsPayload
        });
      }

      setHasExistingPrereq(true);
      setPreviewHtml(liveStyledHtml);
      setExistingUploads((prev) => [...prev, ...stagedUploads]);
      setStagedUploads([]);
      setIsEditing(false); // Switch back to View mode on save

      if (onSaveContent) {
        await onSaveContent({
          domain_id: domainId,
          meta_data: metaData,
          uploads: newUploadsPayload,
          domain_name: selectedDomain.domain_name
        });
      }

      setSnackbar({
        open: true,
        message: `Prerequisites for "${selectedDomain.domain_name}" saved successfully!`,
        severity: "success"
      });
    } catch (err) {
      console.error("Error saving prerequisites:", err);
      setSnackbar({
        open: true,
        message: err?.message || "An error occurred while saving prerequisites.",
        severity: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: readOnly ? "transparent" : "#f8fafc",
        minHeight: readOnly ? "auto" : "100vh",
        p: readOnly ? 0 : { xs: 1.5, sm: 2.5, md: 3 },
        fontFamily: "'Inter', sans-serif"
      }}
    >
      <Box sx={{ maxWidth: 1100, mx: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Top Control Bar: Domain Dropdown placed at the Right Top (hidden in readOnly) */}
        {!readOnly && !propDomainId && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              width: "100%"
            }}
          >
            <Box sx={{ width: { xs: "100%", sm: "320px", md: "360px" } }}>
              <Autocomplete
                size="small"
                options={domainsList}
                loading={isLoadingDomains}
                getOptionLabel={(option) => option.domain_name || ""}
                value={selectedDomain}
                onChange={(event, newValue) => {
                  setSelectedDomain(newValue);
                }}
                isOptionEqualToValue={(option, value) =>
                  String(option.domain_id) === String(value?.domain_id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Domain"
                    placeholder="Choose domain..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <DomainIcon sx={{ color: "#1877f2", fontSize: 20, mr: 1 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {isLoadingDomains ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                      sx: {
                        borderRadius: "10px",
                        fontSize: "0.88rem",
                        fontWeight: 600,
                        backgroundColor: "#ffffff"
                      }
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        )}

        {/* Main Editor Card */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: "14px",
            backgroundColor: "#ffffff",
            overflow: "hidden",
            boxShadow: "0 2px 12px rgba(0,0,0,0.04)"
          }}
        >
          {/* Editor Header: Title on Left, Actions on Right (Hidden for SPOC as modal DialogTitle already displays the title) */}
          {!isSpoc && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2.5,
                py: 2,
                borderBottom: "1px solid #f1f5f9",
                flexWrap: "wrap",
                gap: 2,
                backgroundColor: "#ffffff"
              }}
            >
              {/* Title */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: { xs: "100%", sm: "280px" } }}>
                <DocIcon sx={{ color: "#1877f2", fontSize: 22 }} />
                {isEditing && !readOnly ? (
                  <TextField
                    size="small"
                    fullWidth
                    variant="standard"
                    placeholder="Prerequisites title..."
                    value={documentTitle}
                    onChange={(e) => setDocumentTitle(e.target.value)}
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: "1rem", fontWeight: 700, color: "#1e293b" }
                    }}
                  />
                ) : (
                  <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#1e293b" }}>
                    {documentTitle || (readOnly ? "" : `${selectedDomain?.domain_name || "Domain"} Prerequisites`)}
                  </Typography>
                )}
              </Box>

            {/* Actions: Edit vs Save / Cancel (hidden in readOnly) */}
            {!readOnly && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                {!isEditing ? (
                  /* In View Mode -> Show Edit Button */
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setIsEditing(true)}
                    disabled={!selectedDomain || isLoadingContent}
                    sx={{
                      borderRadius: "8px",
                      backgroundColor: "#1877f2",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.8125rem",
                      px: 2.25,
                      boxShadow: "none",
                      "&:hover": { backgroundColor: "#1465cc", boxShadow: "none" }
                    }}
                  >
                    Edit
                  </Button>
                ) : (
                  /* In Edit Mode -> Show Save, Clear & Cancel Buttons */
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ResetIcon sx={{ fontSize: 16 }} />}
                      onClick={handleClear}
                      disabled={isSubmitting}
                      sx={{
                        borderRadius: "8px",
                        borderColor: "#cbd5e1",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        color: "#64748b",
                        px: 1.5,
                        "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" }
                      }}
                    >
                      Clear
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CancelIcon sx={{ fontSize: 16 }} />}
                      onClick={() => setIsEditing(false)}
                      disabled={isSubmitting}
                      sx={{
                        borderRadius: "8px",
                        borderColor: "#cbd5e1",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        color: "#64748b",
                        px: 1.5,
                        "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" }
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      variant="contained"
                      size="small"
                      startIcon={
                        isSubmitting || isSavingPrereq || isUpdatingPrereq ? (
                          <CircularProgress size={14} sx={{ color: "#ffffff" }} />
                        ) : (
                          <CheckCircleOutline sx={{ fontSize: 16 }} />
                        )
                      }
                      disabled={isSubmitting || isSavingPrereq || isUpdatingPrereq || !selectedDomain || isLoadingContent || isLoadingPrereq}
                      onClick={handleSavePrerequisites}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "#1877f2",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.8125rem",
                        px: 2.25,
                        boxShadow: "none",
                        "&:hover": { backgroundColor: "#1465cc", boxShadow: "none" }
                      }}
                    >
                      {isSubmitting ? "Saving..." : "Save"}
                    </Button>
                  </>
                )}
              </Stack>
            )}
          </Box>
          )}

          {/* Body: Formatted View Mode OR BlockNote Editor */}
          {isLoadingContent || isLoadingPrereq ? (
            <Box
              sx={{
                py: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5
              }}
            >
              <CircularProgress size={32} sx={{ color: "#1877f2" }} />
              <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>
                Loading prerequisites for {selectedDomain?.domain_name || "domain"}...
              </Typography>
            </Box>
          ) : isSpoc ? (
            /* SPOC View Mode: Render ONLY when HTML content is available, otherwise show nothing (only attachment files show below) */
            hasHtmlContent ? (
              <Box
                sx={{
                  backgroundColor: "#ffffff",
                  maxHeight: "750px",
                  overflowY: "auto",
                  p: { xs: 2.5, sm: 4 }
                }}
              >
                <Box
                  sx={{
                    maxWidth: 900,
                    mx: "auto",
                    lineHeight: 1.7,
                    color: "#1e293b"
                  }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </Box>
            ) : !hasFiles ? (
              <Box sx={{ py: 6, textAlign: "center", color: "#94a3b8" }}>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  No prerequisites or attached files available for this domain.
                </Typography>
              </Box>
            ) : null
          ) : isEditing && !readOnly ? (
            /* Active BlockNote Editable Canvas (Styled to visually match View Mode 1:1) */
            <Box
              sx={{
                backgroundColor: "#ffffff",
                minHeight: "450px",
                maxHeight: "750px",
                overflowY: "auto",
                p: { xs: 2.5, sm: 4 },
                "& .bn-container": {
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif !important",
                  backgroundColor: "transparent",
                  maxWidth: 900,
                  mx: "auto"
                },
                "& .bn-editor": {
                  padding: 0,
                  color: "#1e293b",
                  fontSize: "14px",
                  lineHeight: 1.65
                },
                "& h1, & [data-content-type='heading'][data-level='1'] h1, & [data-content-type='heading'][data-level='1'] .bn-inline-content": {
                  fontSize: "22px !important",
                  fontWeight: "700 !important",
                  color: "#0f172a !important",
                  lineHeight: "1.3 !important"
                },
                "& [data-content-type='heading'][data-level='1']": {
                  borderBottom: "2px solid #1877f2 !important",
                  paddingBottom: "6px !important",
                  marginTop: "20px !important",
                  marginBottom: "12px !important"
                },
                "& h2, & [data-content-type='heading'][data-level='2'] h2, & [data-content-type='heading'][data-level='2'] .bn-inline-content": {
                  fontSize: "17px !important",
                  fontWeight: "700 !important",
                  color: "#1e293b !important",
                  marginTop: "18px !important",
                  marginBottom: "8px !important",
                  lineHeight: "1.35 !important"
                },
                "& h3, & [data-content-type='heading'][data-level='3'] h3, & [data-content-type='heading'][data-level='3'] .bn-inline-content": {
                  fontSize: "15px !important",
                  fontWeight: "600 !important",
                  color: "#334155 !important",
                  marginTop: "14px !important",
                  marginBottom: "6px !important"
                },
                "& p, & [data-content-type='paragraph'] .bn-inline-content": {
                  fontSize: "14px !important",
                  lineHeight: "1.65 !important",
                  color: "#334155 !important"
                },
                "& [data-content-type='paragraph']": {
                  marginTop: "6px !important",
                  marginBottom: "10px !important"
                },
                "& [data-content-type='bulletListItem'], & [data-content-type='numberedListItem']": {
                  fontSize: "14px !important",
                  lineHeight: "1.6 !important",
                  color: "#334155 !important",
                  marginBottom: "6px !important"
                }
              }}
            >
              <BlockNoteView editor={editor} theme="light" />
            </Box>
          ) : (
            /* Default Clean View Mode for Trainer */
            hasHtmlContent ? (
              <Box
                sx={{
                  backgroundColor: "#ffffff",
                  maxHeight: "750px",
                  overflowY: "auto",
                  p: { xs: 2.5, sm: 4 }
                }}
              >
                <Box
                  sx={{
                    maxWidth: 900,
                    mx: "auto",
                    lineHeight: 1.7,
                    color: "#1e293b"
                  }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </Box>
            ) : hasFiles ? null : (
              <Box sx={{ py: 8, textAlign: "center", color: "#94a3b8" }}>
                <Typography variant="body2" sx={{ mb: readOnly ? 0 : 2, color: "#64748b" }}>
                  No prerequisites created for this domain yet.
                </Typography>
                {!readOnly && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setIsEditing(true)}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      backgroundColor: "#1877f2",
                      boxShadow: "none",
                      fontWeight: 600
                    }}
                  >
                    Create Prerequisites
                  </Button>
                )}
              </Box>
            )
          )}

          {/* Attachments & Resource Files Section */}
          <Box
            sx={{
              px: { xs: 2.5, sm: 4 },
              py: 2.5,
              borderTop: !hasHtmlContent ? "none" : "1px solid #f1f5f9",
              backgroundColor: "#ffffff"
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
                flexWrap: "wrap",
                gap: 1
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <AttachIcon sx={{ color: "#1877f2", fontSize: 20 }} />
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#1e293b" }}>
                  Prerequisite Files & Attachments
                </Typography>
                {(existingUploads.length + stagedUploads.length > 0) && (
                  <Box
                    component="span"
                    sx={{
                      px: 1,
                      py: 0.2,
                      borderRadius: "12px",
                      backgroundColor: "#e0e7ff",
                      color: "#4338ca",
                      fontSize: "0.72rem",
                      fontWeight: 700
                    }}
                  >
                    {existingUploads.length + stagedUploads.length}
                  </Box>
                )}
              </Stack>

              {isEditing && !readOnly && (
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<UploadIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    borderColor: "#cbd5e1",
                    color: "#334155",
                    "&:hover": { borderColor: "#1877f2", color: "#1877f2" }
                  }}
                >
                  Attach Files
                  <input
                    type="file"
                    multiple
                    hidden
                    onChange={handleAttachmentUpload}
                  />
                </Button>
              )}
            </Box>

            {/* Active S3 Upload Progress Bars */}
            {Object.keys(uploadingProgress).length > 0 && (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {Object.entries(uploadingProgress).map(([fname, percent]) => (
                  <Box
                    key={fname}
                    sx={{
                      p: 1.5,
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                      border: "1px solid #e2e8f0"
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#334155" }}>
                        Uploading {fname} to S3...
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#1877f2" }}>
                        {percent}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={percent}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: "#f1f5f9",
                        "& .MuiLinearProgress-bar": { backgroundColor: "#1877f2", borderRadius: 3 }
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            )}

            {/* Files List: Both Existing and Staged Uploads */}
            {existingUploads.length === 0 && stagedUploads.length === 0 && Object.keys(uploadingProgress).length === 0 ? (
              <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8", fontStyle: "italic" }}>
                No supplementary files attached yet.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {/* Existing Uploads */}
                {existingUploads.map((file, idx) => (
                  <Box
                    key={`existing-${file.id || file.key_name || idx}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.25,
                      px: 1.75,
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                      border: "1px solid #e2e8f0",
                      transition: "all 0.2s ease",
                      "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ overflow: "hidden", mr: 1 }}>
                      <FileIcon sx={{ color: "#1877f2", fontSize: 20 }} />
                      <Typography
                        onClick={() => handlePreviewFile(file)}
                        sx={{
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          color: "#1e293b",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          cursor: "pointer",
                          "&:hover": { color: "#1877f2", textDecoration: "underline" }
                        }}
                      >
                        {file.file_name}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title="Preview file">
                        <IconButton
                          size="small"
                          onClick={() => handlePreviewFile(file)}
                          sx={{ color: "#64748b", "&:hover": { color: "#1877f2" } }}
                        >
                          <PreviewIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download file">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadFile(file)}
                          sx={{ color: "#64748b", "&:hover": { color: "#1877f2" } }}
                        >
                          <DownloadIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      {isEditing && !readOnly && (
                        <Tooltip title="Delete file">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUpload(file, false)}
                            sx={{ color: "#94a3b8", "&:hover": { color: "#ef4444" } }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                ))}

                {/* Staged Uploads (New files ready to be saved) */}
                {stagedUploads.map((file, idx) => (
                  <Box
                    key={`staged-${file.key_name || idx}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.25,
                      px: 1.75,
                      borderRadius: "8px",
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #bbf7d0"
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ overflow: "hidden", mr: 1 }}>
                      <CheckCircleOutline sx={{ color: "#16a34a", fontSize: 18 }} />
                      <Typography
                        onClick={() => handlePreviewFile(file)}
                        sx={{
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          color: "#15803d",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" }
                        }}
                      >
                        {file.file_name}
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          px: 0.8,
                          py: 0.15,
                          borderRadius: "4px",
                          backgroundColor: "#dcfce7",
                          color: "#166534",
                          fontSize: "0.68rem",
                          fontWeight: 700
                        }}
                      >
                        New
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Preview file">
                        <IconButton
                          size="small"
                          onClick={() => handlePreviewFile(file)}
                          sx={{ color: "#16a34a", "&:hover": { color: "#15803d" } }}
                        >
                          <PreviewIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download file">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadFile(file)}
                          sx={{ color: "#16a34a", "&:hover": { color: "#15803d" } }}
                        >
                          <DownloadIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      {isEditing && !readOnly && (
                        <Tooltip title="Delete file">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUpload(file, true)}
                            sx={{ color: "#94a3b8", "&:hover": { color: "#ef4444" } }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {/* Footer Status Bar */}
          {/* <Box
            sx={{
              px: 3,
              py: 1.25,
              borderTop: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "0.75rem",
              color: "#64748b"
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.75rem" }}>
                Domain: <strong>{selectedDomain?.domain_name || "None"}</strong>
              </Typography>
              <span>•</span>
              <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.75rem" }}>
                Mode: <strong>{isEditing ? "Editing" : "Viewing"}</strong>
              </Typography>
            </Stack>

            <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.72rem" }}>
              Domain Prerequisites Editor
            </Typography>
          </Box> */}
        </Paper>
      </Box>

      {/* Delete Confirmation Dialog for Existing Attachments */}
      <DeleteAttachmentConfirmDialog
        open={deleteConfirmDialog.open}
        onClose={() => {
          if (!deleteConfirmDialog.isDeleting) {
            setDeleteConfirmDialog({ open: false, file: null, isDeleting: false });
          }
        }}
        onConfirm={handleConfirmDeleteUpload}
        fileName={deleteConfirmDialog.file?.file_name || ""}
        isDeleting={deleteConfirmDialog.isDeleting}
      />

      {/* In-App Interactive File Preview Lightbox (supports docx, pdf, images, etc.) */}
      <FilePreviewLightbox
        previewFile={lightboxFile}
        onClose={() => setLightboxFile(null)}
        userRole={role || "trainer"}
        showDownload={false}
        onRemoveFile={
          isEditing && !readOnly
            ? (f) => {
                setLightboxFile(null);
                handleDeleteUpload(f, !f.id);
              }
            : null
        }
      />

      {/* Snackbar Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: "8px", fontWeight: 600, fontSize: "0.8rem" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TrainerDocumentEditor;
